<template>
	<div>
		<Dialog :open="showNewFlowModal">
			<DialogContent
				:disable-overlay-close="true"
				:no-close="editFlow.id || getStartedClicked ? false : true"
				@close="showNewFlowModal = false"
			>
				<DialogTitle>Create New Flow</DialogTitle>
				<DialogDescription>Create a new flow to start automating your workflows.</DialogDescription>
				<div v-if="!editFlow.id && tenants?.length > 1" class="w-full">
					<p class="filter-label">Tenant</p>
					<div class="formGroup withIcon">
						<select
							v-model="editFlow.tenant"
							:disabled="editFlow.id || getStartedClicked"
							@change="editFlow.account = null"
						>
							<option :value="null" selected>-- Please select a Tenant --</option>
							<option v-for="tenant in tenants" :key="tenant.id" :value="tenant">
								{{ tenant.companyName }}
							</option>
						</select>
						<span class="fa-light fa-chevron-down"></span>
					</div>
				</div>
				<div v-if="editFlow.tenant && tenants?.length" class="w-full">
					<p class="filter-label">Account</p>
					<div class="formGroup withIcon">
						<select
							v-model="editFlow.accountId"
							:disabled="editFlow.id || getStartedClicked"
							aria-label="Selected Account"
							@change="editFlow.account = accounts.find((a) => a.id === editFlow.accountId)"
						>
							<option :value="null" selected>-- Please select an Account --</option>
							<option v-for="account in tenantAccounts" :key="account.id" :value="account.id">
								{{ account.name }}
							</option>
						</select>
						<span class="fa-light fa-chevron-down"></span>
					</div>
				</div>
				<div v-if="editFlow.tenant && editFlow.account" class="formGroup w-full">
					<p class="filter-label">Flow Name</p>
					<input v-model="editFlow.name" type="text" placeholder="Flow Name" class="w-full" />
				</div>
				<div class="flex gap-2">
					<router-link v-if="!editFlow.id && !getStartedClicked" class="jmiBtn red" to="/flows"
						>Go back
					</router-link>
					<button
						:disabled="!editFlow.tenant || !editFlow.account"
						class="jmiBtn green w-36"
						@click.prevent="getStarted()"
					>
						{{ editFlow.id || getStartedClicked ? 'Save & Close' : 'Get started' }}
					</button>
				</div>
			</DialogContent>
		</Dialog>
		<Dialog :open="showAIFlowModal">
			<DialogContent
				:disable-overlay-close="true"
				class="max-w-2xl bg-white dark:bg-gray-900"
				@close="showAIFlowModal = false"
			>
				<div class="flex flex-col gap-6">
					<div class="flex items-center gap-4">
						<div
							class="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/50"
						>
							<span
								class="fa-light fa-wand-magic-sparkles text-lg text-purple-600 dark:text-purple-400"
							></span>
						</div>
						<div>
							<DialogTitle class="text-xl font-semibold text-gray-900 dark:text-white">
								Generate AI Flow
							</DialogTitle>
							<DialogDescription class="text-sm text-gray-500 dark:text-gray-400">
								Describe your flow in plain language and let AI build it for you
							</DialogDescription>
						</div>
					</div>

					<div v-if="!aiLoading" class="rounded-lg border bg-gray-50 p-4 dark:bg-gray-800/50">
						<h3 class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Example prompts:</h3>
						<div class="space-y-2">
							<button
								v-for="(prompt, index) in examplePrompts"
								:key="index"
								class="block w-full rounded-md bg-white p-3 text-left text-sm text-gray-600 transition-colors hover:bg-purple-50 hover:text-purple-700 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-purple-300"
								@click="selectExample(prompt)"
							>
								{{ prompt }}
							</button>
						</div>
					</div>

					<div v-if="aiLoading" class="rounded-lg border bg-gray-50 p-4 dark:bg-gray-800/50">
						<div class="flex flex-col gap-3">
							<div class="flex items-center justify-between text-sm">
								<div class="flex items-center gap-2 text-gray-600 dark:text-gray-300">
									<span class="relative flex h-2 w-2">
										<span
											class="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75"
										></span>
										<span class="relative inline-flex h-2 w-2 rounded-full bg-purple-500"></span>
									</span>
									{{ aiLoadingMessage }}
								</div>
								<span class="font-mono text-gray-500 dark:text-gray-400"
									>{{ Math.floor(progress) }}%</span
								>
							</div>
							<Progress :model-value="progress" class="h-2 bg-offwhite dark:bg-offmaindark" />
							<div class="grid grid-cols-3 gap-2">
								<div
									v-for="(phase, index) in ['Analysis', 'Design', 'Generation']"
									:key="phase"
									class="flex flex-col items-center gap-1 text-center"
								>
									<div
										class="flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
										:class="
											progress >= (index + 1) * 30
												? 'bg-purple-100 dark:bg-purple-900/50'
												: 'bg-gray-200 dark:bg-gray-700'
										"
									>
										<span
											class="fa-solid fa-check text-xs transition-colors"
											:class="
												progress >= (index + 1) * 30
													? 'text-purple-600 dark:text-purple-400'
													: 'text-gray-400'
											"
										></span>
									</div>
									<span
										class="text-xs font-medium transition-colors"
										:class="
											progress >= (index + 1) * 30
												? 'text-gray-900 dark:text-gray-100'
												: 'text-gray-500 dark:text-gray-400'
										"
									>
										{{ phase }}
									</span>
								</div>
							</div>
						</div>
					</div>
					<div class="w-full">
						<textarea
							v-model="aiPrompt"
							rows="4"
							placeholder="Describe your ideal call flow in plain English... For example: 'Create a flow that greets customers, records their call, asks if they need sales or support, then routes them accordingly.'"
							class="w-full rounded-lg border border-gray-300 bg-white p-4 text-gray-900 transition-colors focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-purple-400"
							:disabled="aiLoading"
							@keydown.meta.enter="generateAIFlow"
							@keydown.ctrl.enter="generateAIFlow"
						></textarea>
						<div class="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
							<span>Press {{ modifierKey }} + Enter to generate</span>
							<span class="flex items-center gap-1">
								<span class="fa-light fa-sparkles"></span>
								Powered by TeleFlow AI
							</span>
						</div>
					</div>

					<div class="flex gap-3">
						<button
							class="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
							@click="showAIFlowModal = false"
						>
							Cancel
						</button>
						<button
							:disabled="!isPromptValid || aiLoading"
							class="flex-1 rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-600 dark:disabled:text-gray-400"
							@click="generateAIFlow"
						>
							<span class="flex items-center justify-center gap-2">
								<span class="fa-light fa-wand-magic-sparkles"></span>
								Generate AI Flow
							</span>
						</button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
		<div class="stage no-bottom-padding flex flex-col sm:flex-row">
			<div class="flex flex-col-reverse items-center gap-2 sm:mb-4 sm:hidden">
				<div class="my-2 flex flex-row-reverse items-center justify-center gap-2">
					<span class="fa-light fa-pencil peer cursor-pointer" @click="showNewFlowModal = true"></span>
					<h1 class="max-w-xs truncate whitespace-nowrap font-medium peer-hover:underline">
						{{ editFlow.name }}
					</h1>
				</div>
				<ul class="flex w-full min-w-min justify-center gap-2">
					<li v-if="$global.hasUserRole('admin') || $global.hasUserRole('beta')">
						<button
							class="jmiBtn btnLg group flex items-center gap-2 border-0 bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg transition-all duration-200 hover:from-purple-600 hover:to-indigo-700 hover:shadow-purple-500/30"
							@click="showAIFlowModal = true"
						>
							<span class="fa-light fa-wand-magic-sparkles text-white"></span>
							<span class="text-white">AI Flow</span>
						</button>
					</li>
					<li>
						<button
							:disabled="!editFlow.tenant || !editFlow.account || !unsavedChanges"
							class="jmiBtn btnLg green"
							@click="save(!editFlow.id)"
						>
							Save
						</button>
					</li>
					<li>
						<button class="jmiBtn btnLg red" @click="routeTo('/flows')">Close</button>
					</li>
				</ul>
			</div>
			<div class="flex flex-row items-center justify-start sm:max-h-[86vh] sm:flex-col">
				<Palette
					v-if="flowNodes.length"
					:surface-id="surfaceId"
					:flow-nodes="flowNodes"
					selector="[data-type]"
					:data-generator="dataGenerator"
				>
				</Palette>
			</div>
			<div class="w-full">
				<div class="flex flex-col sm:min-h-[80vh]">
					<div class="hidden gap-2 pl-2 sm:mb-4 sm:flex">
						<div class="ml-1 flex flex-row-reverse items-center justify-end gap-2">
							<span
								class="fa-light fa-pencil peer cursor-pointer"
								@click="showNewFlowModal = true"
							></span>
							<h1 class="max-w-xs truncate whitespace-nowrap font-medium peer-hover:underline">
								{{ editFlow.name }}
							</h1>
						</div>
						<ul class="flex w-full min-w-min justify-end gap-2">
							<li>
								<button
									class="jmiBtn btnLg group flex items-center gap-2 border-0 bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg transition-all duration-200 hover:from-purple-600 hover:to-indigo-700 hover:shadow-purple-500/30"
									@click="showAIFlowModal = true"
								>
									<span class="fa-light fa-wand-magic-sparkles text-white"></span>
									<span class="text-white">AI Flow</span>
								</button>
							</li>
							<li>
								<button
									:disabled="!editFlow.tenant || !editFlow.account || !unsavedChanges"
									class="jmiBtn btnLg green"
									@click="save(!editFlow.id)"
								>
									Save
								</button>
							</li>
							<li>
								<button class="jmiBtn btnLg red" @click="routeTo('/flows')">Close</button>
							</li>
						</ul>
					</div>
					<div class="flex h-[80vh] w-full flex-col">
						<div
							class="relative flex flex-grow overflow-hidden rounded-md border bg-white dark:border-black/50 dark:bg-offmaindark"
						>
							<jsplumb-toolkit
								v-if="flowNodes.length"
								ref="toolkitComponent"
								:surface-id="surfaceId"
								:render-params="renderParams()"
								:view="viewParams()"
								:toolkit-params="toolkitParams()"
							>
							</jsplumb-toolkit>
							<Controls :surface-id="surfaceId"></Controls>
							<jsplumb-miniview :surface-id="surfaceId"></jsplumb-miniview>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue'
import moment from 'moment'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { loadSurface } from '@jsplumbtoolkit/browser-ui-vue3'
import {
	isPort,
	ForceDirectedLayout,
	DotEndpoint,
	LassoPlugin,
	AnchorLocations,
	CONNECTOR_TYPE_ORTHOGONAL,
	EVENT_TAP,
	EVENT_CANVAS_CLICK,
	consume,
	uuid,
	EVENT_CONNECTION_MOUSEOVER
} from '@jsplumbtoolkit/browser-ui'

import Controls from '../components/flow/Controls.vue'
import Palette from '../components/flow/Palette.vue'
import BaseNode from '../components/flow/BaseNode.vue'
import NoteNode from '../components/flow/NoteNode.vue'
import flowsApi from '../scripts/flows/flows.api'
import accountsApi from '../scripts/accounts/accounts.api'
import { markRaw, computed, onMounted } from 'vue'
import * as lodash from 'lodash'

let toolkitComponent
let toolkit
let surface

export default defineComponent({
	components: {
		Controls,
		Palette,
		Dialog,
		DialogContent,
		DialogTitle,
		DialogDescription,
		Progress
	},
	inject: ['user', 'userRoles', 'tenants'],
	provide() {
		return {
			selectedFlowTenant: computed({
				get: () => this.editFlow.tenant?.id
			}),
			selectedFlowAccount: computed({
				get: () => this.editFlow.account?.id
			})
		}
	},
	beforeRouteLeave(to, from, next) {
		if (this.unsavedChanges) {
			if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
				return next()
			}
			return next(false)
		}
		return next()
	},
	data: function () {
		return {
			accounts: [],
			showNewFlowModal: false,
			showAIFlowModal: false,
			surfaceId: Date.now().toString(),
			settingsOpen: false,
			configuringNode: null,
			unsavedChanges: false,
			getStartedClicked: false,
			aiPrompt: '',
			aiLoading: false,
			aiLoadingMessage: '',
			progress: 0,
			isMac: /Mac|iPhone|iPod|iPad/.test(navigator.userAgent),
			dataGenerator: (el) => {
				const node_id = this.getNextNodeIdForType(el.getAttribute('data-type'))

				return {
					type: el.getAttribute('data-type'),
					name: el.getAttribute('data-label'),
					config: { ...JSON.parse(el.getAttribute('data-default-config') || '{}'), node_id }
				}
			},
			editFlow: {
				data: {},
				tenant: null,
				account: null,
				name: `New Flow ${moment().format('YY-MM-DD HH:mm:ss')}`,
				accountId: null
			},
			configuringNodeId: null,
			configuringNodeData: null,
			flowNodes: [],
			examplePrompts: [
				'Create a call flow that routes customers to sales or support based on their selection',
				'Make a flow that simply enables call recording with AI transcription, then plays a message to the caller, followed by routing to an agent via a SIM.',
				'Design a customer service IVR that queues for 5 minutes before going to voicemail'
			]
		}
	},
	computed: {
		modifierKey() {
			return this.isMac ? '⌘' : 'Ctrl'
		},
		tenantAccounts() {
			return this.accounts
				.filter((account) => account.tenant?.id === this.editFlow.tenant.id)
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		isPromptValid() {
			return this.aiPrompt && this.aiPrompt.trim().length >= 10
		}
	},
	watch: {},
	async created() {
		this.flowNodes = await flowsApi.getAllNodes()

		window.addEventListener('beforeunload', (event) => {
			if (this.unsavedChanges) {
				if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
					event.preventDefault()
				}
			}
		})
	},
	mounted() {
		toolkitComponent = this.$refs.toolkitComponent

		loadSurface(this.surfaceId, async (s) => {
			surface = s
			toolkit = surface.toolkitInstance

			toolkit.bind('node:updated', this.checkUnsavedChanges)
			toolkit.bind('node:removed', this.checkUnsavedChanges)
			toolkit.bind('node:added', this.checkUnsavedChanges)
			toolkit.bind('edge:added', this.checkUnsavedChanges)
			toolkit.bind('edge:removed', this.checkUnsavedChanges)

			accountsApi.getAll({ limit: 2500 }).then(async (result) => {
				this.accounts = result.data.results

				if (this.$route.params.id !== 'new') {
					const result = await flowsApi.getById(this.$route.params.id)
					this.editFlow = result.data

					this.editFlow.data.nodes = this.editFlow.data.nodes.map((node) => {
						if (!node.config?.node_id) {
							if (!node.config) node.config = {}
							node.config.node_id = this.getNextNodeIdForType(node.type)
						}
						return node
					})

					if (this.editFlow.account) this.editFlow.accountId = this.editFlow.account.id

					toolkit.load({ data: JSON.parse(JSON.stringify(this.editFlow.data)), type: 'json' })
				} else {
					if (this.tenants?.length === 1) this.editFlow.tenant = this.tenants[0]
					if (this.accounts?.length && !this.tenants?.length) {
						this.editFlow.account = this.accounts[0]
						this.editFlow.tenant = this.accounts[0].tenant
					}

					this.showNewFlowModal = true

					toolkit.load({
						data: {
							nodes: [
								{
									id: uuid(),
									type: 'entrypoint',
									name: 'Entry Point',
									top: 50,
									left: 20,
									config: {
										node_id: 'entrypoint'
									}
								}
							],
							edges: []
						},
						type: 'json'
					})
				}

				setTimeout(() => surface.setZoomRange([0.25, 2]), 100)
			})
		})
	},
	methods: {
		getNextNodeIdForType(type) {
			if (type === 'entrypoint') return 'entrypoint'

			const nodes = toolkit.getNodes().filter((node) => node.data?.type === type)
			let nextId = nodes.length + 1

			while (nodes.find((node) => node.data?.config?.node_id === `${type}${nextId}`)) {
				nextId++
			}

			return `${type}${nextId}`
		},
		routeTo(path) {
			this.$router.push(path)
		},
		getStarted() {
			this.getStartedClicked = true
			this.showNewFlowModal = false
		},
		checkUnsavedChanges() {
			if (!this.readonly) {
				let old = JSON.parse(JSON.stringify(this.editFlow.data))
				let current = toolkit.exportData({ type: 'json' })

				old.nodes = old.nodes?.map((node) => {
					delete node.top
					delete node.left
					return node
				})

				current.nodes = current.nodes?.map((node) => {
					delete node.top
					delete node.left
					return node
				})

				this.unsavedChanges = !lodash.isEqual(old, current)
			}
		},
		removeUnsavedChangesListener() {
			this.unsavedChanges = false
			toolkit.unbind('node:updated', this.checkUnsavedChanges)
			toolkit.unbind('node:removed', this.checkUnsavedChanges)
			toolkit.unbind('node:added', this.checkUnsavedChanges)
			toolkit.unbind('edge:added', this.checkUnsavedChanges)
			toolkit.unbind('edge:removed', this.checkUnsavedChanges)
			window.removeEventListener('beforeunload', this.checkUnsavedChanges)
		},
		save(close) {
			if (this.editFlow.accountId)
				this.editFlow.account = this.accounts.find((a) => a.id === this.editFlow.accountId)

			const data = toolkit.exportData({ type: 'json' })
			this.editFlow.data = JSON.parse(JSON.stringify(data))
			if (!this.editFlow.id) {
				flowsApi
					.create(this.editFlow)
					.then((result) => {
						if (result?.error)
							this.$swal({
								title: 'Error',
								icon: 'error',
								text: result.error,
								confirmButtonText: 'Try Again'
							})
						else if (close) {
							this.removeUnsavedChangesListener()
							this.routeTo('/flows')
						} else this.checkUnsavedChanges()
					})
					.catch(console.error)
			} else {
				flowsApi
					.update(this.editFlow)
					.then((result) => {
						if (result?.error)
							this.$swal({
								title: 'Error',
								icon: 'error',
								text: result.error,
								confirmButtonText: 'Try Again'
							})
						else if (close) {
							this.removeUnsavedChangesListener()
							this.routeTo('/flows')
						} else this.checkUnsavedChanges()
					})
					.catch(console.error)
			}
		},
		toolkitParams() {
			return {
				beforeConnect: (source, target) => {
					return isPort(source) && isPort(target) && source !== target
				}
			}
		},
		viewParams() {
			const nodeList = {
				...this.flowNodes.reduce((acc, node) => {
					acc[node.type] = {
						component: markRaw(BaseNode),
						inject: {
							spec: node,
							flowNodes: this.flowNodes
						}
					}
					return acc
				}, {}),
				note: {
					component: NoteNode,
					inject: {
						spec: {}
					}
				}
			}

			return {
				nodes: nodeList,
				ports: {
					output: {
						edgeType: 'output',
						maxConnections: 1,
						anchor: [AnchorLocations.Right]
					},
					input: {
						edgeType: 'input',
						maxConnections: -1,
						anchor: [AnchorLocations.Left]
					}
				},
				edges: {
					default: {
						detachable: false,
						connector: {
							type: CONNECTOR_TYPE_ORTHOGONAL,
							options: {
								cornerRadius: 18
							}
						},
						cssClass: 'jtk-schema-common-edge opacity-40 hover:opacity-80',
						events: {
							[EVENT_CONNECTION_MOUSEOVER]: function (params) {
								console.log('Mouse over', params)
							}
						},
						overlays: [
							{
								type: 'Custom',
								options: {
									cssClass: 'connector-delete',
									create: (component) => {
										const container = document.createElement('div')
										container.style.position = 'relative'

										const button = document.createElement('button')
										button.className =
											'absolute bg-stone-500/20 hover:bg-stone-500/50 backdrop-blur-md text-white rounded-lg size-7 flex items-center justify-center'
										button.style.top = '50%'
										button.style.left = '50%'
										button.style.transform = 'translate(-50%, -50%)'
										button.innerHTML = '<span class="fa-light fa-close"></span>'

										button.addEventListener('click', (event) => {
											event.stopPropagation()
											toolkit.removeEdge(component.edge.id)
										})

										container.appendChild(button)
										return container
									},
									location: 0.1,
									events: {
										[EVENT_TAP]: function (params) {
											consume(params.e)
											toolkit.removeEdge(params.edge.id)
										}
									}
								}
							}
						]
					}
				}
			}
		},
		renderParams() {
			return {
				wheel: {
					reverse: true
				},
				dragOptions: {
					filter: ['jtk-delete-button', 'jtk-add-button', 'jtk-schema-add'].join(',')
				},
				plugins: [LassoPlugin.type],
				events: {
					[EVENT_CANVAS_CLICK]: function (e) {
						toolkit.clearSelection()
					}
				},
				zoomToFit: this.$route.params.id !== 'new',
				layout: {
					type: ForceDirectedLayout.type,
					options: {
						padding: { x: 150, y: 150 }
					}
				},
				zoomRange: [0.75, 1],
				defaults: {
					endpoint: {
						type: DotEndpoint.type,
						options: {
							cssClass: '.jtk-schema-endpoint'
						}
					}
				},
				consumeRightClick: false
			}
		},
		selectExample(prompt) {
			this.aiPrompt = prompt
		},
		async generateAIFlow() {
			if (!this.isPromptValid) return

			const result = await this.$swal({
				title: 'Generate AI Flow',
				text: 'This action will overwrite your existing flow configuration. Are you sure you want to continue?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Yes, generate flow',
				cancelButtonText: 'Cancel'
			})

			if (!result.isConfirmed) return

			this.aiLoading = true
			this.progress = 0

			const loadingMessages = [
				'Initialising AI model...',
				'Analysing your requirements...',
				'Processing natural language...',
				'Designing flow architecture...',
				'Generating optimal routing paths...',
				'Configuring call settings...',
				'Validating flow structure...',
				'Finalising configuration...'
			]

			try {
				this.aiLoadingMessage = loadingMessages[0]
				this.progress = 5

				const apiPromise = flowsApi.aiGenerate({
					prompt: this.aiPrompt,
					tenantId: this.editFlow.tenant?.id,
					accountId: this.editFlow.account?.id
				})

				const animateProgress = async () => {
					for (let i = 1; i < loadingMessages.length - 1; i++) {
						this.aiLoadingMessage = loadingMessages[i]
						const targetProgress = 10 + i * 12

						await new Promise((resolve) => {
							const startProgress = this.progress
							const startTime = Date.now()
							const duration = 1500 + Math.random() * 1000

							const animate = () => {
								const elapsed = Date.now() - startTime
								const ratio = Math.min(elapsed / duration, 1)

								const easeRatio =
									ratio < 0.5 ? 4 * ratio * ratio * ratio : 1 - Math.pow(-2 * ratio + 2, 3) / 2

								this.progress = startProgress + (targetProgress - startProgress) * easeRatio

								if (ratio < 1) {
									requestAnimationFrame(animate)
								} else {
									resolve()
								}
							}

							requestAnimationFrame(animate)
						})
					}
				}

				const progressAnimation = animateProgress()

				const apiResponse = await apiPromise

				if (apiResponse.error || (!apiResponse.data && !apiResponse.result)) {
					throw new Error(apiResponse.error || 'Error generating flow')
				}

				await progressAnimation

				this.aiLoadingMessage = loadingMessages[loadingMessages.length - 1]
				this.progress = 95

				let flowData
				try {
					const rawData = apiResponse.data || apiResponse.result
					flowData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData
				} catch (parseError) {
					console.error('Error parsing flow data:', parseError)
					throw new Error('Invalid flow data received from API')
				}

				toolkit.clear()
				toolkit.load({ data: flowData, type: 'json' })

				this.progress = 100
				this.aiLoadingMessage = 'Flow generated successfully!'
				await new Promise((resolve) => setTimeout(resolve, 800))

				this.showAIFlowModal = false
				this.checkUnsavedChanges()
			} catch (error) {
				console.error('Error generating AI flow:', error)
				this.$swal({
					title: 'Flow Generation Failed',
					text:
						error.message === 'Error generating flow'
							? 'Unable to generate flow with the provided requirements. Please try rephrasing your request.'
							: 'There was an error generating your flow. Please try again.',
					icon: 'error'
				})
			} finally {
				this.aiLoading = false
				this.aiLoadingMessage = ''
				this.aiPrompt = ''
			}
		}
	}
})
</script>
